/* ════════════════════════════════════════════════════
 *  <kik-award> — press-logo → quote switcher.
 *
 *  Each press logo can carry its own quote. Every quote is rendered up front,
 *  stacked in one grid cell (see the section's {% stylesheet %}) so the card
 *  never shifts height — selecting a logo just crossfades which quote is shown.
 *  Logos without a quote are inert. The first logo with a quote is rendered
 *  active server-side; this component only wires up the interaction.
 *
 *  Autoplay (data-autoplay / data-autoplay-speed): auto-rotates the active
 *  logo → quote on a timer. Pauses on hover / keyboard focus, restarts on
 *  manual selection, and is skipped when the visitor prefers reduced motion.
 *
 *  Swipe (touch): a horizontal swipe on the quote area advances to the next /
 *  previous quote — the same #select path a logo tap uses, so the crossfade and
 *  the logo slider stay in sync. Vertical scrolls and short drags are ignored.
 *  Swiping the logo carousel itself also drives the quote: the slider's
 *  slideChange makes the newly-scrolled-in logo active (mobile only).
 *
 *  Markup contract (rendered by sections/kik-award.liquid) — logos and quotes
 *  are paired by the block id:
 *    <kik-award data-autoplay="true" data-autoplay-speed="5000">
 *      <div class="section-kik-award__quotes">
 *        <p data-kik-award-quote="{block.id}" class="… is-active?" aria-hidden>…</p>
 *      </div>
 *      …
 *      <div data-kik-award-logo="{block.id}" role="button" tabindex="0"
 *           aria-pressed="true|false" class="… is-active?">…</div>
 *    </kik-award>
 * ════════════════════════════════════════════════════ */

if (!customElements.get('kik-award')) {
  class KikAward extends HTMLElement {
    #autoplayEnabled = false;
    #autoplayPaused = false;
    #autoplayDelay = 5000;
    #autoplayTimer = null;

    connectedCallback() {
      this.logos = Array.from(this.querySelectorAll('[data-kik-award-logo]'));
      this.quotes = Array.from(this.querySelectorAll('[data-kik-award-quote]'));

      if (this.logos.length === 0 || this.quotes.length === 0) return;

      this.onActivate = this.onActivate.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onSlideChange = this.onSlideChange.bind(this);
      this.pause = this.pause.bind(this);
      this.resume = this.resume.bind(this);

      for (const logo of this.logos) {
        logo.addEventListener('click', this.onActivate);
        logo.addEventListener('keydown', this.onKeydown);
      }

      this.#setupQuoteSwipe();
      this.#setupSlider();
      this.#setupAutoplay();
    }

    disconnectedCallback() {
      this.#stopAutoplay();

      for (const logo of this.logos || []) {
        logo.removeEventListener('click', this.onActivate);
        logo.removeEventListener('keydown', this.onKeydown);
      }

      if (this.quotesEl) {
        this.quotesEl.removeEventListener('touchstart', this.onTouchStart);
        this.quotesEl.removeEventListener('touchend', this.onTouchEnd);
      }

      this.slider?.off?.('slideChange', this.onSlideChange);

      this.removeEventListener('mouseenter', this.pause);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('focusin', this.pause);
      this.removeEventListener('focusout', this.resume);
    }

    onActivate(event) {
      this.#select(event.currentTarget);
    }

    onKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault(); // Space would otherwise scroll the page
      this.#select(event.currentTarget);
    }

    onTouchStart(event) {
      const touch = event.changedTouches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
    }

    onTouchEnd(event) {
      if (this.touchStartX == null) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - this.touchStartX;
      const dy = touch.clientY - this.touchStartY;
      this.touchStartX = null;

      // Ignore vertical scrolls and short drags — only a decisive horizontal
      // swipe (≥40px, more X than Y) advances the quote.
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
      this.#advance(dx < 0 ? 1 : -1); // swipe left → next, right → previous
    }

    /* Swiping the logo carousel scrolls a new logo to the front (activeIndex);
       adopt it as the active logo so its quote shows. Skipped on desktop
       (swiper disabled) and when the front slide has no quote or is already
       active — the latter also breaks the #select → slideTo → slideChange loop. */
    onSlideChange() {
      const swiper = this.slider;
      if (!swiper || !swiper.enabled) return;

      const slide = swiper.slides[swiper.activeIndex];
      if (!slide || !slide.hasAttribute('data-kik-award-logo')) return;
      if (slide.classList.contains('is-active')) return;

      this.#select(slide);
    }

    pause() {
      this.#autoplayPaused = true;
      this.#stopAutoplay();
    }

    resume() {
      this.#autoplayPaused = false;
      this.#startAutoplay();
    }

    /* Wire a horizontal swipe on the quote stack to the next / previous quote.
       The quote <p>s are pointer-events:none, so the touch lands on the
       .section-kik-award__quotes container. Passive listeners — we never
       preventDefault, so vertical page scrolling is untouched. */
    #setupQuoteSwipe() {
      this.quotesEl = this.querySelector('.section-kik-award__quotes');
      if (!this.quotesEl || this.logos.length < 2) return;

      this.quotesEl.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.quotesEl.addEventListener('touchend', this.onTouchEnd, { passive: true });
    }

    #setupAutoplay() {
      if (this.dataset.autoplay !== 'true') return;
      if (this.logos.length < 2) return; // nothing to rotate through
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.#autoplayDelay = Number(this.dataset.autoplaySpeed) || 5000;
      this.#autoplayEnabled = true;

      this.addEventListener('mouseenter', this.pause);
      this.addEventListener('mouseleave', this.resume);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.resume);

      this.#startAutoplay();
    }

    #startAutoplay() {
      if (!this.#autoplayEnabled || this.#autoplayPaused) return;
      this.#stopAutoplay();
      this.#autoplayTimer = setTimeout(() => this.#advance(), this.#autoplayDelay);
    }

    #stopAutoplay() {
      if (!this.#autoplayTimer) return;
      clearTimeout(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }

    #advance(direction = 1) {
      const current = this.logos.findIndex((logo) => logo.classList.contains('is-active'));
      const base = current < 0 ? 0 : current;
      const count = this.logos.length;
      const next = this.logos[(base + direction + count) % count];
      this.#select(next); // #select re-arms the timer
    }

    #select(logo) {
      if (!logo) return;
      const id = logo.dataset.kikAwardLogo;

      for (const item of this.logos) {
        const active = item === logo;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', active ? 'true' : 'false');
      }

      for (const quote of this.quotes) {
        const active = quote.dataset.kikAwardQuote === id;
        quote.classList.toggle('is-active', active);
        quote.setAttribute('aria-hidden', active ? 'false' : 'true');
      }

      this.#syncSlider(logo);
      this.#startAutoplay(); // reset the countdown after any selection
    }

    /* Grab the logo carousel's Swiper instance and listen for slideChange.
       The <kik-swiper> is defined + upgraded before this component, so its
       .swiper is usually ready here; a bounded rAF retry covers the case where
       the Swiper lib finishes initialising a tick later. */
    #setupSlider() {
      const el = this.querySelector('kik-swiper');
      if (!el) return;

      let tries = 0;
      const attach = () => {
        if (el.swiper) {
          this.slider = el.swiper;
          el.swiper.on('slideChange', this.onSlideChange);
        } else if (tries++ < 20) {
          requestAnimationFrame(attach);
        }
      };
      attach();
    }

    /* On mobile the logos are a 3-up <kik-swiper> carousel, so an off-screen
       active logo must be scrolled into view. On desktop Swiper is disabled
       (all logos already show) — swiper.enabled is false there, so we skip. */
    #syncSlider(logo) {
      const swiper = this.querySelector('kik-swiper')?.swiper;
      if (!swiper || !swiper.enabled) return;

      const index = Array.from(swiper.slides).indexOf(logo);
      if (index < 0) return;

      swiper.slideTo(index);
    }
  }

  customElements.define('kik-award', KikAward);
}
